// Gemini video-understanding adapter (§10.5). Implements VideoUnderstandingPort
// over the Files API: upload a media file once, poll to ACTIVE, generate against
// the file reference (fps at the media-part level), and delete when done. The
// adapter owns transport only — it never builds business prompts or imports the
// ingest domain. The Google SDK is reached through a narrow structural client so
// tests inject a fake and never hit the network.

import * as fs from "node:fs";
import { createRequire } from "node:module";
import type {
  Candidate,
  GenerateContentResponsePromptFeedback,
  GenerateContentResponseUsageMetadata,
} from "@google/genai";

import {
  classificationOf,
  contextOf,
  type GeminiPolicyReason,
  type ProviderSafetyRating,
} from "../../failures.js";
import type { LlmModelConfig, LlmProviderConfig } from "../../llm/config.js";
import {
  LlmTransportError,
  type VideoFileHandle,
  type VideoMediaInput,
  type VideoGenerationRequest,
  type VideoGenerationResponse,
  type VideoUnderstandingPort,
  type TokenUsage,
} from "../../llm/types.js";
import { redactCredential, resumableUpload } from "./gemini-upload.js";
import { httpError, httpStatusOf, networkError } from "./failure-mapping.js";
import { addTokenUsage, withTransientRetry } from "./retry.js";

// The subset of @google/genai we use. Kept structural so a fake client satisfies
// it in tests without importing the SDK.
export interface GeminiFile {
  name?: string;
  uri?: string;
  mimeType?: string;
  state?: string;
  error?: { message?: string } | undefined;
}

export interface GeminiGenerateResponse {
  text?: string;
  candidates?: Candidate[];
  promptFeedback?: GenerateContentResponsePromptFeedback;
  usageMetadata?: GenerateContentResponseUsageMetadata;
}

// finishReasons that yield empty text but are terminal: retry/resume never clears them.
const FILTERED_FINISH = new Set([
  "SAFETY",
  "PROHIBITED_CONTENT",
  "RECITATION",
  "BLOCKLIST",
  "SPII",
  "IMAGE_SAFETY",
  "IMAGE_PROHIBITED_CONTENT",
  "IMAGE_RECITATION",
  "ESCALATION",
]);

const TERMINAL_RESPONSE_FINISH = new Set([
  "LANGUAGE",
  "OTHER",
  "MALFORMED_FUNCTION_CALL",
  "IMAGE_OTHER",
  "NO_IMAGE",
  "UNEXPECTED_TOOL_CALL",
  "TOO_MANY_TOOL_CALLS",
  "MISSING_THOUGHT_SIGNATURE",
  "MALFORMED_RESPONSE",
]);

function policyReason(raw: string): GeminiPolicyReason {
  switch (raw) {
    case "SAFETY":
    case "PROHIBITED_CONTENT":
    case "BLOCKLIST":
    case "SPII":
    case "RECITATION":
    case "IMAGE_SAFETY":
    case "IMAGE_PROHIBITED_CONTENT":
    case "IMAGE_RECITATION":
    case "ESCALATION":
    case "OTHER":
      return raw;
    case "":
    case "BLOCK_REASON_UNSPECIFIED":
    case "FINISH_REASON_UNSPECIFIED":
      return "UNSPECIFIED";
    default:
      return "UNKNOWN";
  }
}

function safetyRatings(
  values: Array<{ category?: string; probability?: string; blocked?: boolean }> | undefined,
): ProviderSafetyRating[] | undefined {
  if (!values?.length) return undefined;
  return values.map((rating) => ({
    category: rating.category ?? "UNSPECIFIED",
    probability: rating.probability ?? "UNSPECIFIED",
    blocked: rating.blocked ?? false,
  }));
}

// Gemini reports usageMetadata.{promptTokenCount,candidatesTokenCount}. promptTokenCount
// already includes the (large) video token count. Missing → undefined → counted as zero.
function geminiUsage(meta: unknown): TokenUsage {
  if (typeof meta !== "object" || meta === null) return {};
  const m = meta as Record<string, unknown>;
  const num = (v: unknown): number | undefined => (typeof v === "number" ? v : undefined);
  return { inputTokens: num(m["promptTokenCount"]), outputTokens: num(m["candidatesTokenCount"]) };
}

export interface GeminiClient {
  files: {
    upload(params: { file: string; config?: { mimeType?: string } }): Promise<GeminiFile>;
    get(params: { name: string }): Promise<GeminiFile>;
    delete(params: { name: string }): Promise<unknown>;
  };
  models: {
    generateContent(params: Record<string, unknown>): Promise<GeminiGenerateResponse>;
    generateContentStream?(params: Record<string, unknown>): Promise<AsyncIterable<GeminiGenerateResponse>>;
  };
}

// Poll cadence while an uploaded file is still PROCESSING. Overall budget is the
// provider timeout; tests inject clients that return ACTIVE immediately.
const POLL_INTERVAL_MS = 2000;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// Map an SDK/network error to a transport error with the same retry policy as
// the other adapters: retry only transient network/timeout/408/429/5xx.
function toTransportError(
  error: unknown,
  signal: AbortSignal | undefined,
  what: string,
  credential: string,
  provider: string,
  model: string,
): LlmTransportError {
  if (error instanceof LlmTransportError) {
    return new LlmTransportError(error.code, redactCredential(error.message, credential), {
      retryable: error.retryable,
      details: error.details.map((detail) => redactCredential(detail, credential)),
      attempts: error.attempts,
      usage: error.usage,
      classification: classificationOf(error.failure),
      context: { ...contextOf(error.failure), provider, model, operation: what },
      modelFailures: error.modelFailures,
    });
  }
  const message = redactCredential(error instanceof Error ? error.message : String(error), credential);
  const status = httpStatusOf(error);
  const context = { provider, model, operation: what };
  if (status !== null) {
    return httpError(status, `Gemini ${what} failed.`, message.slice(0, 500), context);
  }
  return networkError(error, signal, `Gemini ${what} failed.`, message.slice(0, 500), context);
}

export class GeminiVideoModel implements VideoUnderstandingPort {
  private readonly client: GeminiClient;

  constructor(
    private readonly provider: LlmProviderConfig,
    private readonly model: LlmModelConfig,
    private readonly apiKey: string,
    client?: GeminiClient,
  ) {
    this.client = client ?? createGoogleClient(apiKey, provider.timeoutMs, provider.baseUrl);
  }

  async prepare(
    localPath: string,
    mimeType: string,
    mode: "file-api" | "base64",
    signal?: AbortSignal,
  ): Promise<VideoMediaInput> {
    if (mode === "base64") {
      return { kind: "base64", data: fs.readFileSync(localPath, "base64"), mimeType };
    }
    const file = await this.uploadFile(localPath, mimeType, signal);
    return { kind: "file-api", file };
  }

  private async uploadFile(localPath: string, mimeType: string, signal?: AbortSignal): Promise<VideoFileHandle> {
    const { value: file } = await withTransientRetry(this.provider.maxAttempts, async () => {
      try {
        return await this.client.files.upload({ file: localPath, config: { mimeType } });
      } catch (error) {
        throw toTransportError(error, signal, "upload", this.apiKey, this.model.provider, this.model.model);
      }
    });
    const active = await this.pollActive(file, signal);
    if (!active.name || !active.uri) {
      throw new LlmTransportError("invalid_response", "Gemini upload returned no file reference.", {
        context: { provider: this.model.provider, model: this.model.model, operation: "upload" },
      });
    }
    return { name: active.name, uri: active.uri, mimeType: active.mimeType ?? mimeType };
  }

  private async pollActive(initial: GeminiFile, signal?: AbortSignal): Promise<GeminiFile> {
    let file = initial;
    const deadline = Date.now() + this.provider.timeoutMs;
    // Media-processing failure is terminal — never retried (§12.3).
    while ((file.state ?? "").toUpperCase() === "PROCESSING") {
      if (signal?.aborted) {
        throw new LlmTransportError("cancelled", "Gemini upload aborted.", {
          context: { provider: this.model.provider, model: this.model.model, operation: "file_poll" },
        });
      }
      if (Date.now() >= deadline) {
        throw new LlmTransportError("timeout", "Gemini file processing timed out.", {
          retryable: true,
          classification: { failure_type: "timeout", failure_code: "media_processing_timeout" },
          context: { provider: this.model.provider, model: this.model.model, operation: "file_poll" },
        });
      }
      await delay(POLL_INTERVAL_MS);
      if (!file.name) {
        throw new LlmTransportError("invalid_response", "Gemini upload returned no file name to poll.", {
          context: { provider: this.model.provider, model: this.model.model, operation: "file_poll" },
        });
      }
      try {
        file = await this.client.files.get({ name: file.name });
      } catch (error) {
        throw toTransportError(error, signal, "file_poll", this.apiKey, this.model.provider, this.model.model);
      }
    }
    if ((file.state ?? "").toUpperCase() === "FAILED") {
      throw new LlmTransportError("invalid_response", "Gemini file processing failed.", {
        details: [redactCredential(file.error?.message ?? "unknown media error", this.apiKey)],
        classification: { failure_type: "media", failure_code: "media_processing_failed" },
        context: { provider: this.model.provider, model: this.model.model, operation: "file_poll" },
      });
    }
    return file;
  }

  async generate(request: VideoGenerationRequest): Promise<VideoGenerationResponse> {
    if (request.media.kind === "local-fallback") {
      throw new LlmTransportError("invalid_response", "A local fallback video cannot be sent to Gemini.");
    }
    const config: Record<string, unknown> = {
      // High media resolution reads small on-screen text (subtitles, name cards)
      // and fine visual anchors (§10.3).
      mediaResolution: "MEDIA_RESOLUTION_HIGH",
      maxOutputTokens: request.maxOutputTokens,
    };
    if (request.system?.trim()) config["systemInstruction"] = request.system;
    if (request.responseJsonSchema) {
      config["responseMimeType"] = "application/json";
      config["responseJsonSchema"] = request.responseJsonSchema;
    }
    const params: Record<string, unknown> = {
      model: this.model.model,
      contents: [{
        role: "user",
        parts: [
          request.media.kind === "file-api"
            ? { fileData: { fileUri: request.media.file.uri, mimeType: request.media.file.mimeType }, videoMetadata: { fps: request.fps } }
            : { inlineData: { data: request.media.data, mimeType: request.media.mimeType }, videoMetadata: { fps: request.fps } },
          { text: request.user },
        ],
      }],
      config,
    };

    const { value, attempts, failedUsage } = await withTransientRetry(this.provider.maxAttempts, async () => {
      let response: GeminiGenerateResponse;
      try {
        if (this.client.models.generateContentStream) {
          let text = "";
          let latest: GeminiGenerateResponse = {};
          const stream = await this.client.models.generateContentStream(params);
          for await (const chunk of stream) {
            if (chunk.text) text += chunk.text;
            latest = {
              ...latest,
              ...chunk,
              usageMetadata: chunk.usageMetadata ?? latest.usageMetadata,
              promptFeedback: chunk.promptFeedback ?? latest.promptFeedback,
              candidates: chunk.candidates ?? latest.candidates,
            };
          }
          response = { ...latest, text };
        } else {
          response = await this.client.models.generateContent(params);
        }
      } catch (error) {
        throw toTransportError(error, request.signal, "generate", this.apiKey, this.model.provider, this.model.model);
      }
      const candidate = response.candidates?.[0];
      const finishReason = candidate?.finishReason ?? "";
      const blockReason = response.promptFeedback?.blockReason ?? "";
      const usage = geminiUsage(response.usageMetadata);
      const baseContext = { provider: this.model.provider, model: this.model.model, operation: "generate" };
      if (finishReason === "MAX_TOKENS") {
        throw new LlmTransportError("truncated", "Gemini output was truncated.", { usage, context: baseContext });
      }
      if (blockReason) {
        throw new LlmTransportError("content_filtered", `Gemini blocked the prompt (blockReason=${blockReason}).`, {
          usage,
          classification: { failure_type: "policy", failure_code: "content_blocked" },
          context: {
            ...baseContext,
            provider_source: "prompt",
            provider_reason: policyReason(blockReason),
            ...(policyReason(blockReason) === "UNKNOWN" ? { provider_reason_raw: blockReason } : {}),
            safety_ratings: safetyRatings(response.promptFeedback?.safetyRatings),
          },
        });
      }
      if (FILTERED_FINISH.has(finishReason)) {
        throw new LlmTransportError("content_filtered", `Gemini filtered the response (finishReason=${finishReason}).`, {
          usage,
          classification: { failure_type: "policy", failure_code: "content_blocked" },
          context: {
            ...baseContext,
            provider_source: "candidate",
            provider_reason: policyReason(finishReason),
            finish_message: candidate?.finishMessage,
            safety_ratings: safetyRatings(candidate?.safetyRatings),
          },
        });
      }
      const text = (response.text ?? "").trim();
      if (!text) {
        if (TERMINAL_RESPONSE_FINISH.has(finishReason)) {
          throw new LlmTransportError("invalid_response", `Gemini returned no usable response (finishReason=${finishReason}).`, {
            usage,
            context: { ...baseContext, provider_reason_raw: finishReason, finish_message: candidate?.finishMessage },
          });
        }
        // Empty body with a normal/blank finishReason is a transient generation hiccup
        // (observed ~1/15 episodes); retry it — a resume of the same episode succeeds.
        throw new LlmTransportError("empty_response", `Gemini returned an empty response (finishReason=${finishReason || "none"}).`, {
          retryable: true,
          usage,
          context: baseContext,
        });
      }
      if (request.responseJsonSchema) {
        try {
          JSON.parse(text);
        } catch {
          throw new LlmTransportError("invalid_response", "Gemini returned malformed JSON for a structured video response.", {
            retryable: true,
            usage,
            context: baseContext,
          });
        }
      }
      return { text, finishReason, usage, raw: response };
    });
    return { text: value.text, finishReason: value.finishReason, attempts, usage: addTokenUsage(failedUsage, value.usage), raw: value.raw };
  }

  async release(media: VideoMediaInput, signal?: AbortSignal): Promise<void> {
    if (media.kind === "base64" || media.kind === "local-fallback") return;
    try {
      await this.client.files.delete({ name: media.file.name });
    } catch (error) {
      // Cleanup is best-effort (§10.5); a failed delete must not fail the run.
      void error;
      void signal;
    }
  }
}

// Lazily import the SDK so the dependency is only loaded when a video route is
// actually exercised (keeps text-only runs free of @google/genai).
const require = createRequire(import.meta.url);
function createGoogleClient(apiKey: string, timeoutMs: number, baseUrl: string | null): GeminiClient {
  const { GoogleGenAI } = require("@google/genai") as typeof import("@google/genai");
  const sdk = new GoogleGenAI({
    apiKey,
    ...(baseUrl ? { httpOptions: { baseUrl, timeout: timeoutMs } } : {}),
  }) as unknown as {
    files: { get(p: { name: string }): Promise<GeminiFile>; delete(p: { name: string }): Promise<unknown> };
    models: {
      generateContent(p: Record<string, unknown>): Promise<GeminiGenerateResponse>;
      generateContentStream(p: Record<string, unknown>): Promise<AsyncIterable<GeminiGenerateResponse>>;
    };
  };
  const proxyUrl = (process.env.https_proxy ?? process.env.HTTPS_PROXY ?? process.env.http_proxy ?? process.env.HTTP_PROXY ?? "").trim();
  // A proxy is set but Node's built-in fetch (which the SDK's get/delete/generate
  // use) ignores https_proxy unless NODE_USE_ENV_PROXY=1 — so upload (our proxied
  // https) succeeds and the very next poll fails with "file poll failed". Warn
  // once with the exact fix rather than let it look like a network error.
  if (proxyUrl && !(process.env.NODE_USE_ENV_PROXY ?? "").trim()) {
    process.stderr.write(
      `⚠️  检测到代理 ${proxyUrl}，但未设 NODE_USE_ENV_PROXY=1。\n` +
      `   Gemini 上传会成功、随后的文件轮询/生成会挂（Node 内置 fetch 默认不走代理）。\n` +
      `   请在命令前加 NODE_USE_ENV_PROXY=1 重跑。\n`,
    );
  }
  // upload goes through Node https (undici rejects the resumable response's
  // content-length behind a proxy); get/delete/generateContent use the SDK.
  return {
    files: {
      upload: (p) => resumableUpload({
        apiKey,
        filePath: p.file,
        mimeType: p.config?.mimeType ?? "video/mp4",
        timeoutMs,
        proxyUrl,
        baseUrl,
      }),
      get: (p) => sdk.files.get(p),
      delete: (p) => sdk.files.delete(p),
    },
    models: {
      generateContent: (p) => sdk.models.generateContent(p),
      generateContentStream: (p) => sdk.models.generateContentStream(p),
    },
  };
}
